<template>
	<EditorShell>
		<!-- Header -->
		<CLayoutEditorHeader
			:isLoading="isLoading"
			:headline="template ? template.name : null"
			:saveButton="$t('templates.save')"
			@back:click="$router.back()"
			@save:click="updateTemplate"
		>
			<EditorSaveChangesPrompt :isVisible="hasChanged" slot="middle" />
		</CLayoutEditorHeader>

		<!-- Wrapper -->
		<JBox class="s-max-w-5xl s-mx-auto" :class="{ 's-max-w-full s-px-4': showPreview }">
			<!-- Preview switcher -->
			<EditorPreviewSwitcher
				:showPreview="showPreview"
				:label="$t('templates.graphQlVariables')"
				:mockDataLabel="$t('templates.previewMockData')"
				@preview:toggle="showPreview = $event"
				@preview:open="renderPreview"
				@render:click="renderPreview"
				v-model="previewVariables"
				:mockData="previewMockData"
				@input="invalidateDataForPreview"
				@mockDataUpdate="setMockData"
				class="s-h-10 s-mt-4"
			>
				<JButton @click="downloadPreviewFile" variant="default-light-sm" slot="post-buttons" class="s-mr-2">
					{{ $t("templates.downloadPreview") }}
				</JButton>
			</EditorPreviewSwitcher>

			<!-- Body -->
			<JFlex class="s-mb-4 s-mt-1 s-items-stretch" style="height: 1270px">
				<!-- Editors -->
				<JBox
					class="s-bg-white s-rounded-md s-border s-border-gray-300 s-px-5 s-pb-5 s-z-0"
					:class="[showPreview ? 's-w-6/12' : 's-w-full']"
				>
					<JFlex class="s-justify-between s-py-2">
						<JLabel class="s-block s-text-black s-font-semibold s-leading-tight s-text-xl s-pt-1">{{
							selectedView === "body"
								? $t("templates.body")
								: selectedView === "header"
								? $t("templates.header")
								: $t("templates.footer")
						}}</JLabel>
						<JFlex>
							<JFlex class="s-items-center" @click="onModeChange">
								<JToggle :value="isDevMode" class="s-mr-2" />
								<JLabel class="s-font-semibold s-text-sm s-text-gray-900">
									{{ $t("templates.devMode") }}
								</JLabel>
							</JFlex>
							<JButton variant="white-sm" class="s-font-semibold s-ml-2" @click="selectedView = 'header'">{{
								$t("templates.header")
							}}</JButton>
							<JButton variant="white-sm" class="s-ml-2 s-font-semibold" @click="selectedView = 'body'">{{
								$t("templates.body")
							}}</JButton>
							<JButton variant="white-sm" class="s-ml-2 s-font-semibold" @click="selectedView = 'footer'">{{
								$t("templates.footer")
							}}</JButton>
						</JFlex>
					</JFlex>

					<CRichTextEditor
						v-if="!isDevMode"
						:content="selectedViewHtml"
						@input="updateHtml"
						:height="1200"
						fetchFilesFor="templateAssets"
						:hasLiquidPicker="true"
						:hasFilePicker="true"
					/>
					<template v-else>
						<CCodeEditor ref="codeEditor" class="s-w-full" height="1200px" language="html" v-model="selectedViewHtml" />
					</template>
				</JBox>

				<!-- Preview -->
				<Transition enter-active-class="animated fadeIn faster">
					<PdfPreview
						v-if="showPreview"
						class="s-w-6/12 s-ml-4 s-rounded s-border s-border-gray-300 s-bg-gray-300 s-overflow-scroll"
						:showPreview="showPreview"
						:previewData="previewResult"
						:isLoading="isPreviewLoading"
						@error="onPreviewError"
					/>
				</Transition>
			</JFlex>

			<!-- Document Settings -->
			<EditorAccordion :headline="$t('templates.document_settings')" class="s-mb-4">
				<EditorDocumentSettings v-model="template">
					<PdfTemplateSettings :payload="template" @input="event => template === event" class="s-mt-6" />
				</EditorDocumentSettings>
			</EditorAccordion>

			<!-- Template Graphql Queries -->
			<EditorAccordion :headline="$t('core.graphQlQuery')" class="s-mb-4">
				<CGraphQlQueryLibrary v-model="template.graphQlQuery" @input="invalidateDataForPreview" />
			</EditorAccordion>

			<!-- Delete template -->
			<CDialogProvider
				:headline="$t('templates.delete_template')"
				:description="$t('templates.delete_dialog')"
				:cancelButton="$t('templates.continueEditing')"
				:confirmButton="$t('templates.delete_template')"
				@confirm="deleteTemplate"
			>
				<JFlex class="s-justify-end s-mb-4" slot-scope="{ openDialog }">
					<JButton variant="error" @click="openDialog">{{ $t("templates.delete_template") }} </JButton>
				</JFlex>
			</CDialogProvider>

			<!-- Debug Console -->
			<EditorDebugConsole :errors="errors" />
		</JBox>
	</EditorShell>
</template>

<script>
import EditorShell from "../components/EditorShell"
import PdfTemplateSettings from "../components/pdfHtml/PdfTemplateSettings"
import PdfPreview from "../components/pdfHtml/PdfPreview"
import EditorAccordion from "../components/EditorAccordion"
import EditorSaveChangesPrompt from "../components/EditorSaveChangesPrompt"
import EditorDocumentSettings from "../components/EditorDocumentSettings"
import EditorPreviewSwitcher from "../components/EditorPreviewSwitcher"
import EditorDebugConsole from "../components/EditorDebugConsole"
import { preventBrowserBackButton } from "../utils/preventBrowserBackButton"
import isEqual from "lodash.isequal"
import { deepClone } from "@/utils/"
import { addHintToLiquidErrorString } from "../utils/addHintToLiquidErrorString"
import api from "@/api/api"
import { query, mutation } from "../api/queries"

export default {
	components: {
		EditorShell,
		PdfTemplateSettings,
		EditorDocumentSettings,
		EditorPreviewSwitcher,
		EditorDebugConsole,
		PdfPreview,
		EditorAccordion,
		EditorSaveChangesPrompt,
	},
	props: {
		id: {
			type: String,
		},
	},
	data: () => ({
		isLoading: false,
		isDevMode: localStorage.getItem("htmlToPdfEditorDevMode") === "true",
		previewMockData: null,
		template: {},
		originalTemplate: {},
		selectedView: "body",

		showPreview: false,
		isPreviewLoading: false,
		previewVariables: `{"where":{"id":""}}`,
		previewData: null,
		previewResult: null,
		test: null,

		errors: [],
	}),

	computed: {
		selectedViewHtml: {
			get() {
				if (this.selectedView === "body") {
					return this.template.html
				} else if (this.selectedView === "header") {
					return this.template.headerSettings.html
				} else {
					return this.template.footerSettings.html
				}
			},
			set(val) {
				if (this.selectedView === "body") {
					this.template.html = val
				} else if (this.selectedView === "header") {
					this.template.headerSettings.html = val
				} else {
					this.template.footerSettings.html = val
				}
			},
		},
		hasChanged() {
			return !isEqual(this.template, this.originalTemplate)
		},
		headerHTML() {
			const headerHtml = this.template?.headerSettings?.html
			const a = "</"
			return headerHtml?.includes("<script>")
				? headerHtml
				: `<html>
					<head>
						<script>
							function subst() {
								var vars = {};
								var query_strings_from_url = document.location.search.substring(1).split('&');
								for (var query_string in query_strings_from_url) {
									if (query_strings_from_url.hasOwnProperty(query_string)) {
										var temp_var = query_strings_from_url[query_string].split('=', 2); vars[temp_var[0]] = decodeURI(temp_var[1]);
									}
								}
								var css_selector_classes = ['page', 'frompage', 'topage', 'webpage', 'section', 'subsection', 'date', 'isodate', 'time', 'title', 'doctitle', 'sitepage', 'sitepages'];
								for (var css_class in css_selector_classes) {
									if (css_selector_classes.hasOwnProperty(css_class)) {
										var element = document.getElementsByClassName(css_selector_classes[css_class]);
										for (var j = 0; j < element.length; ++j) {
											element[j].textContent = vars[css_selector_classes[css_class]];
										}
									}
								}
								var page = Number(vars.page);
								var topage = Number(vars.topage);

								if(page == 1) {
									document.querySelector('.sub-header').style.display = 'none'
								}
								if(page > 1) {
									document.querySelector('.main-header').style.display = 'none'
								}
								if(page < topage) {
									document.querySelector('.last-page').style.display = 'none'
								}
								if(page == topage) {
									document.querySelector('.all-but-last-page').style.display = 'none'
								}
							}` +
						a +
						'script></head><body style="border:0; margin: 0;" onload="subst()">' +
						headerHtml +
						"</body></html>"
		},

		footerHTML() {
			const footerHtml = this.template?.footerSettings?.html
			const a = "</"
			return footerHtml?.includes("<script>")
				? footerHtml
				: `<html>
					<head>
						<script>
							function subst(){
								var vars = {};
								var query_strings_from_url = document.location.search.substring(1).split('&');
								for (var query_string in query_strings_from_url) {
									if (query_strings_from_url.hasOwnProperty(query_string)) {
										var temp_var = query_strings_from_url[query_string].split('=', 2);
										vars[temp_var[0]] = decodeURI(temp_var[1]);
									}
								}
								var css_selector_classes = ['page', 'frompage', 'topage', 'webpage', 'section', 'subsection', 'date', 'isodate', 'time', 'title', 'doctitle', 'sitepage', 'sitepages'];
								for (var css_class in css_selector_classes) {
									if (css_selector_classes.hasOwnProperty(css_class)) {
										var element = document.getElementsByClassName(css_selector_classes[css_class]);
										for (var j = 0; j < element.length; ++j) {
											element[j].textContent = vars[css_selector_classes[css_class]];
										}
									}
								}
								var page = Number(vars.page);
								var topage = Number(vars.topage);

								if(page == topage) {
									document.querySelector('.sub-footer').style.display = 'none'
								}
								if(page < topage) {
									document.querySelector('.last-page').style.display = 'none';
									document.querySelector('.main-footer').style.display = 'none'
								}
								if(page > 1) {
									document.querySelector('.first-page').style.display = 'none'
								}
								if(page == 1) {
									document.querySelector('.all-but-first-page').style.display = 'none'
								}
							}` +
						a +
						'script></head><body style="border:0; margin: 0;" onload="subst()">' +
						footerHtml +
						"</body></html>"
		},
	},

	methods: {
		/**
		 * Escapes HTML content to prevent Cloudflare blocking
		 */
		escapeHtml(html) {
			if (!html || typeof html !== 'string') {
				return html;
			}

			return html
				.replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;')
				.replace(/"/g, '&quot;')
				.replace(/'/g, '&#39;');
		},

		/**
		 * Escapes HTML content in updateWkhtmltopdfTemplate mutation variables
		 */
		escapeWkhtmltopdfHtml(variables) {
			if (!variables || typeof variables !== 'object') {
				return variables;
			}

			const escaped = { ...variables };

			// Handle input object
			if (escaped.input && typeof escaped.input === 'object') {
				escaped.input = { ...escaped.input };

				// Escape html field in input
				if (escaped.input.html && typeof escaped.input.html === 'string') {
					escaped.input.html = this.escapeHtml(escaped.input.html);
				}

				// Handle headerSettings
				if (escaped.input.headerSettings && typeof escaped.input.headerSettings === 'object') {
					escaped.input.headerSettings = { ...escaped.input.headerSettings };
					if (escaped.input.headerSettings.html && typeof escaped.input.headerSettings.html === 'string') {
						escaped.input.headerSettings.html = this.escapeHtml(escaped.input.headerSettings.html);
					}
				}

				// Handle footerSettings
				if (escaped.input.footerSettings && typeof escaped.input.footerSettings === 'object') {
					escaped.input.footerSettings = { ...escaped.input.footerSettings };
					if (escaped.input.footerSettings.html && typeof escaped.input.footerSettings.html === 'string') {
						escaped.input.footerSettings.html = this.escapeHtml(escaped.input.footerSettings.html);
					}
				}
			}

			return escaped;
		},

		onModeChange() {
			this.isDevMode = !this.isDevMode
			localStorage.setItem("htmlToPdfEditorDevMode", this.isDevMode)
		},
		updateHtml(value) {
			if (this.selectedView === "body") {
				this.template.html = value
			} else if (this.selectedView === "header") {
				this.template.headerSettings.html = value
			} else {
				this.template.footerSettings.html = value
			}
		},
		onPreviewError(error) {
			this.setErrors()
			this.setErrors([error.message])
		},
		async fetchTemplate() {
			if (this.isLoading) return
			this.setErrors()

			this.isLoading = true
			const res = await api({
				query: query.fetchWkhtmltopdfTemplateData,
				variables: {
					where: { id: this.id },
					limit: 1,
				},
			})
			this.isLoading = false

			if (!res?.data?.templates?.list?.[0]) {
				this.setErrors(res?.errors?.map(item => item?.message) | [this.$t("templates.noTemplate")])
				return
			}

			this.template = res?.data?.templates?.list?.[0] ?? {}
			this.originalTemplate = deepClone(res?.data?.templates?.list?.[0] ?? {})
		},

		async updateTemplate() {
			if (this.isLoading) return
			this.setErrors()

			this.isLoading = true

			// Prepare variables with HTML escaping
			const variables = {
				templateId: this.id,
				input: {
					name: this.template?.name,
					description: this.template?.description,
					passwordLiquid: this.template?.passwordLiquid ?? null,
					marginSettings: this.template?.marginSettings,
					html: this.template?.html,
					logicalId: this.template?.logicalId,
					headerSettings: {
						html: `<!DOCTYPE html>${this.headerHTML}`,
						spacing: this.template?.headerSettings?.spacing,
					},
					footerSettings: {
						html: `<!DOCTYPE html>${this.footerHTML}`,
						spacing: this.template?.footerSettings?.spacing,
					},
					graphQlQuery: this.template?.graphQlQuery,
				},
			}

			// Apply HTML escaping
			const escapedVariables = this.escapeWkhtmltopdfHtml(variables)

			const res = await api({
				query: mutation.updateWkhtmltopdfTemplate,
				variables: escapedVariables,
			})
			this.isLoading = false

			// When error
			if (res.data?.updateWkhtmltopdfTemplate?.status !== "success") {
				this.setErrors(res?.data?.updateWkhtmltopdfTemplate?.errors || res?.errors?.map(item => item?.message))
				return
			}

			this.originalTemplate = deepClone(this.template)
		},

		invalidateDataForPreview() {
			this.previewData = null
		},

		setMockData(data) {
			this.previewMockData = data || "{}"
		},

		async getMemoizedDataForPreview() {
			if (this.previewData) {
				return Promise.resolve({ isSuccess: true, errors: null, data: this.previewData })
			}

			const query = this?.template?.graphQlQuery ?? ""
			let variables = { limit: 1 }

			if (!query) return Promise.resolve({ isSuccess: true, errors: null, data: null })

			try {
				variables = { ...variables, ...JSON.parse(this.previewVariables) }
			} catch (e) {
				return Promise.resolve({ isSuccess: false, errors: [e], data: null })
			}

			const res = await api({ query, variables })

			if (res?.errors) {
				return Promise.resolve({ isSuccess: false, errors: res?.errors?.map(item => item.message), data: null })
			}

			this.previewData = res?.data
			return Promise.resolve({ isSuccess: true, data: res?.data, errors: null })
		},

		async renderPreview() {
			if (this.isPreviewLoading) return
			this.isPreviewLoading = true
			this.previewResult = ""
			this.setErrors()

			const { isSuccess, errors, data } = await this.getMemoizedDataForPreview()
			if (!isSuccess) {
				this.setErrors(errors, { hint: this.$t("templates.problemWithGql") })
				this.isPreviewLoading = false
				return
			}

			// Merge object to pass Settings and Data
			let contentJsonString = {}
			if (data) contentJsonString = { ...data }
			if (this.previewMockData) contentJsonString = { ...contentJsonString, ...JSON.parse(this.previewMockData || "{}") }

			const res = await api({
				query: query.renderWkhtmltopdf,
				variables: {
					html: this.template?.html,
					marginSettings: this.template?.marginSettings,
					headerSettings: {
						html: `<!DOCTYPE html>${this.headerHTML}`,
						spacing: this.template?.headerSettings?.spacing,
					},
					footerSettings: {
						html: `<!DOCTYPE html>${this.footerHTML}`,
						spacing: this.template?.footerSettings?.spacing,
					},
					input: {
						name: "data",
						contentJsonString: JSON.stringify(contentJsonString),
					},
				},
			})
			this.isPreviewLoading = false

			if (res?.data?.renderWkhtmltopdf?.status !== "success") {
				this.setLiquidErrors(res?.data?.renderWkhtmltopdf?.errors || res?.errors?.map(item => item?.message))
				return
			}

			this.previewResult = new Uint8Array(res?.data?.renderWkhtmltopdf?.value)
		},

		async deleteTemplate() {
			if (this.isLoading) return
			this.setErrors()

			this.isLoading = true
			const res = await api({
				query: mutation.deleteTemplate,
				variables: { templateId: this.id },
			})

			if (res.data?.deleteTemplate?.status !== "success") {
				this.setErrors(res?.data?.deleteTemplate?.errors || res?.errors?.map(item => item?.message))
				return
			}

			this.$router.push({ name: "ViewTemplatesHome" })
		},

		downloadPreviewFile() {
			const byteArray = this.previewResult
			const a = window.document.createElement("a")

			a.href = window.URL.createObjectURL(new Blob([byteArray], { type: "application/pdf" }))
			a.download = this?.template?.name

			document.body.appendChild(a) // Append anchor to body
			a.click()
			document.body.removeChild(a) // Remove anchor from body
		},

		setLiquidErrors(errors = []) {
			const output = errors.map(item =>
				addHintToLiquidErrorString({
					message: item,
					templateSource: this.template?.html,
				})
			)
			this.errors = output
		},

		setErrors(errors = [], { hint = "" } = {}) {
			console.log({ hint })
			this.errors = errors?.map(err => ({ message: err, hint }))
		},
	},

	watch: {
		showPreview: {
			handler() {
				if (this.isDevMode) {
					// Wait for DOM updates
					setTimeout(() => {
						// Monaco editor resize API
						this.$refs.codeEditor.$refs.monacoEditor.getEditor().layout()
					}, 50)
				}
			},
		},
	},

	created() {
		this.fetchTemplate()
	},

	beforeRouteLeave(to, from, next) {
		preventBrowserBackButton({ next, showDialog: this.hasChanged })
	},
}
</script>
